'use client';
import { useState, type FormEvent } from 'react';
import Button from '../../miniapp/ui-kit/Button';
import type { ContentBook } from '../content';
import { getApiPath } from '../pathPrefix';

export default function PartnershipForm({
  copy,
  enabled,
  phone,
  language,
}: {
  copy: ContentBook['form'];
  enabled: boolean;
  phone: string;
  language: string;
}) {
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'rateLimit'>('idle');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending || !enabled) return;
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    setPending(true);
    setStatus('idle');
    try {
      const response = await fetch(getApiPath('/web/partnership'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ ...values, language }),
      });
      if (!response.ok) {
        setStatus(response.status === 429 ? 'rateLimit' : 'error');
        return;
      }
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
    } finally {
      setPending(false);
    }
  };
  const inputClass =
    'mt-2 w-full min-w-0 rounded-lg border border-border-solid bg-bg px-4 py-3 text-base text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring';
  return (
    <section
      id="application"
      className="scroll-mt-40 rounded-3xl bg-primary p-6 md:p-10"
      aria-labelledby="application-title"
    >
      <h2 id="application-title" className="m-0 text-2xl font-bold md:text-3xl">
        {copy.title}
      </h2>
      <p className="max-w-[65ch] leading-relaxed text-text-secondary">{copy.intro}</p>
      {!enabled && (
        <p className="max-w-[65ch] leading-relaxed">
          {copy.unavailable}{' '}
          <a className="text-accent underline underline-offset-4" href={`tel:${phone}`}>
            {phone}
          </a>
        </p>
      )}
      <form onSubmit={(event) => void submit(event)} className="mt-6 max-w-3xl" aria-busy={pending}>
        <fieldset
          disabled={!enabled || pending}
          className="m-0 grid min-w-0 gap-5 border-0 p-0 sm:grid-cols-2"
        >
          {(['name', 'company', 'phone', 'email'] as const).map((field) => (
            <label key={field} className="text-sm font-semibold">
              {copy[field]}
              <input
                name={field}
                required
                type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                autoComplete={
                  { name: 'name', company: 'organization', phone: 'tel', email: 'email' }[field]
                }
                maxLength={field === 'email' ? 254 : field === 'phone' ? 40 : 120}
                className={inputClass}
              />
            </label>
          ))}
          <label className="sm:col-span-2 text-sm font-semibold">
            {copy.project}
            <textarea
              name="project"
              required
              minLength={10}
              maxLength={4000}
              rows={5}
              className={inputClass}
            />
          </label>
          <label className="hidden" aria-hidden="true">
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </fieldset>
        <p className="text-sm leading-relaxed text-text-secondary">{copy.privacy}</p>
        <Button type="submit" loading={pending} disabled={!enabled || status === 'success'}>
          {pending ? copy.sending : copy.submit}
        </Button>
        <p
          role="status"
          aria-live="polite"
          className={`mb-0 leading-relaxed ${status === 'success' ? 'text-success' : 'text-error'}`}
        >
          {status !== 'idle' ? copy[status] : ''}
        </p>
      </form>
    </section>
  );
}
